import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { 
  Play, Loader2, Sparkles, CheckCircle, Video, Wand2, Archive,
  Plus, Trash2, Download, RefreshCw, Sliders, Layout, Type, Palette, Check,
  Layers, FolderOpen, AlertTriangle, Cpu, BarChart2, Briefcase, Calendar, 
  Shield, Link, Home, Share2, Activity, CheckSquare, Clock, AlertCircle, FileText, Info, Eye, ExternalLink, Copy, Sliders as SlidersIcon
} from 'lucide-react';

export interface VideoAutomationEngineProps {
  sessionRole?: 'PUBLIC' | 'USER' | 'CLIENT' | 'ADMIN' | 'SUPERADMIN';
  setSessionRole?: React.Dispatch<React.SetStateAction<any>>;

  // Brand assets
  brandAssets: {
    company: string;
    tagline: string;
    primaryLogo: string;
    secondaryLogo: string;
    phone: string;
    primaryEmail: string;
    supportEmail: string;
    websiteUrl: string;
    themePrimary: string;
    themeAccent1: string;
    themeAccent2: string;
    themeHighlight: string;
    ctaText: string;
    footerDisclosure: string;
    socialHandles: string;
    voiceoverTone: string;
    brandIntro: string;
  };
  setBrandAssets: React.Dispatch<React.SetStateAction<any>>;

  // Video dataset
  videosList: Array<{
    id: string;
    title: string;
    type: string;
    goal: string;
    targetAudience: string;
    serviceOrProduct: string;
    duration: string;
    aspectRatio: string;
    tone: string;
    cta: string;
    useLogo: boolean;
    useWebsite: boolean;
    usePhone: boolean;
    useEmail: boolean;
    useVoiceover: boolean;
    useSubtitles: boolean;
    useThumbnail: boolean;
    scenePrompt: string;
    notes: string;
    priority: string;
    status: string;
    pagePlacement: string;
    category: string;
    views: number;
    createdAt: string;
    script?: { hook: string; problem: string; solution: string; benefit: string; cta: string };
    voiceoverScript?: string;
    subtitleText?: string;
    thumbnailText?: string;
    thumbnailPrompt?: string;
    suggestedFilename?: string;
    videoUrl?: string;
    guideId?: string;
  }>;
  setVideosList: React.Dispatch<React.SetStateAction<any>>;

  // Schedulers
  schedulers: Array<{ id: string; name: string; frequency: string; lastRun: string; nextRun: string; active: boolean; outputType: string; mode: string }>;
  setSchedulers: React.Dispatch<React.SetStateAction<any>>;

  // Reusable templates
  automationTemplates: Array<{
    id: string;
    name: string;
    sceneCount: number;
    sceneDuration: string;
    textOverlays: string;
    motionStyle: string;
    ctaStyle: string;
    voiceoverLength: string;
    subtitleStyle: string;
    thumbnailStyle: string;
    outputAspectRatio: string;
    recommendedUsage: string;
  }>;

  // Diagnostics and core callbacks
  auditLogs: Array<{ id: string; time: string; user: string; action: string; status: string }>;
  setAuditLogs: React.Dispatch<React.SetStateAction<any>>;
  activeVideoObject: any;
  setActiveVideoObject: React.Dispatch<React.SetStateAction<any>>;

  // Main UI form state handlers so changes map perfectly
  formVideoTitle: string;
  setFormVideoTitle: (v: string) => void;
  formVideoType: string;
  setFormVideoType: (v: string) => void;
  formVideoGoal: string;
  setFormVideoGoal: (v: string) => void;
  formTargetAudience: string;
  setFormTargetAudience: (v: string) => void;
  formServiceOrProduct: string;
  setFormServiceOrProduct: (v: string) => void;
  formDuration: string;
  setFormDuration: (v: string) => void;
  formAspectRatio: string;
  setFormAspectRatio: (v: string) => void;
  formTone: string;
  setFormTone: (v: string) => void;
  formCta: string;
  setFormCta: (v: string) => void;
  formUseLogo: boolean;
  setFormUseLogo: (v: boolean) => void;
  formUsePhone: boolean;
  setFormUsePhone: (v: boolean) => void;
  formUseEmail: boolean;
  setFormUseEmail: (v: boolean) => void;
  formUseWebsite: boolean;
  setFormUseWebsite: (v: boolean) => void;
  formUseVoiceover: boolean;
  setFormUseVoiceover: (v: boolean) => void;
  formUseSubtitles: boolean;
  setFormUseSubtitles: (v: boolean) => void;
  formUseThumbnail: boolean;
  setFormUseThumbnail: (v: boolean) => void;
  formScenePrompt: string;
  setFormScenePrompt: (v: string) => void;
  formNotes: string;
  setFormNotes: (v: string) => void;
  formPriority: string;
  setFormPriority: (v: string) => void;
  formStatus: string;
  setFormStatus: (v: string) => void;

  // Render & duplicate warnings callbacks
  duplicateWarning: string | null;
  setDuplicateWarning: (v: string | null) => void;
  triggerVerifyDuplicate: () => void;
  handleRunAutomationRender: (title: string, createSuite: boolean) => void;
}

export default function VideoAutomationEngine({
  sessionRole, setSessionRole: setSessionRoleInParent,
  brandAssets, setBrandAssets,
  videosList, setVideosList,
  schedulers, setSchedulers,
  automationTemplates,
  auditLogs, setAuditLogs,
  activeVideoObject, setActiveVideoObject,
  formVideoTitle, setFormVideoTitle,
  formVideoType, setFormVideoType,
  formVideoGoal, setFormVideoGoal,
  formTargetAudience, setFormTargetAudience,
  formServiceOrProduct, setFormServiceOrProduct,
  formDuration, setFormDuration,
  formAspectRatio, setFormAspectRatio,
  formTone, setFormTone,
  formCta, setFormCta,
  formUseLogo, setFormUseLogo,
  formUsePhone, setFormUsePhone,
  formUseEmail, setFormUseEmail,
  formUseWebsite, setFormUseWebsite,
  formUseVoiceover, setFormUseVoiceover,
  formUseSubtitles, setFormUseSubtitles,
  formUseThumbnail, setFormUseThumbnail,
  formScenePrompt, setFormScenePrompt,
  formNotes, setFormNotes,
  formPriority, setFormPriority,
  formStatus, setFormStatus,
  duplicateWarning, setDuplicateWarning,
  triggerVerifyDuplicate,
  handleRunAutomationRender
}: VideoAutomationEngineProps) {

  // Active sub-section state (1 of 10 working sections requested)
  // Options: BRAND_ASSETS, VIDEO_GENERATOR, VIDEO_PACK, VIDEO_QUEUE, TEMPLATE_LIBRARY, GUIDES_SYNC, HOMEPAGE_MEDIA, SOCIAL_QUEUE, PUBLISH_SCHEDULER, OVERVIEW_DASHBOARD
  const [activeSection, setActiveSection] = useState<string>('OVERVIEW_DASHBOARD');

  // Dynamic local session role state that falls back to prop if provided
  const [localSessionRole, setLocalSessionRole] = useState<'PUBLIC' | 'USER' | 'CLIENT' | 'ADMIN' | 'SUPERADMIN'>(sessionRole || 'ADMIN');

  useEffect(() => {
    if (sessionRole) {
      setLocalSessionRole(sessionRole);
    }
  }, [sessionRole]);

  const currentRole = localSessionRole;
  const isPublic = currentRole === 'PUBLIC';

  // Extended features states
  const [activeHomepageHeroId, setActiveHomepageHeroId] = useState<string>("vid-pack-1-default");
  const [heroOverlayTemplate, setHeroOverlayTemplate] = useState<string>("Cosmic Grid Matrix");
  const [socialQueue, setSocialQueue] = useState([
    { id: "soc-1", videoId: "v-default-3", title: "Nexus Search Campaign (Reel Form)", platform: "Instagram Reel", scheduledTime: "Wednesday 15:00", status: "Scheduled", isPosted: false, caption: "Build on Node-01. Stop building integrations by hand! NexoraX syncs payments instantly. Click link in bio. #SaaS #AI #automation" },
    { id: "soc-2", videoId: "v-default-2", title: "Quantum Billing Process (Teaser Clip)", platform: "TikTok", scheduledTime: "Wednesday 18:00", status: "Approved", isPosted: false, caption: "Automate accounting splits. Process raw figures instantly under 10% maintenance terms! #accounting #fintech #NexoraX" },
    { id: "soc-3", videoId: "v-default-1", title: "Corporate Onboarding Walkthrough (Explainer Short)", platform: "YouTube Short", scheduledTime: "Thursday 12:00", status: "Draft", isPosted: false, caption: "Zero-Budget Services Guide: Secure prime positions in Google Workspace with direct support! 💎 #hiring #workspace #jobs" }
  ]);

  // Alert simulation state
  const [simulatedAlerts, setSimulatedAlerts] = useState([
    { id: "a-1", type: "CRITICAL", msg: "Wednesday Ledger Liquidity sync pending for NC-887095", time: "11:00" },
    { id: "a-2", type: "SUCCESS", msg: "Google Workspace API: Settlement pipeline active on Sort 04-06-05 Account 31396515", time: "10:15" },
    { id: "a-3", type: "WARNING", msg: "Sitemap index duplicate sensor detected 1 near-match tag ('Promo v1')", time: "09:40" }
  ]);

  // Automated Templates Live Loader Simulation
  const [isFetchingTemplates, setIsFetchingTemplates] = useState(false);
  const [templateSearchQuery, setTemplateSearchQuery] = useState("");
  const [templateFilterRatio, setTemplateFilterRatio] = useState("ALL");
  const [templateFetchLogs, setTemplateFetchLogs] = useState<string[]>([]);
  const [templatesHasLoadedOnce, setTemplatesHasLoadedOnce] = useState(false);

  const simulateFetchTemplates = () => {
    setIsFetchingTemplates(true);
    setTemplateFetchLogs([`[HANDSHAKE] Handshake sent to Secure Cloud Registry...`]);
    setAuditLogs(prev => [
      { id: "log-tf-" + Date.now(), time: new Date().toTimeString().split(' ')[0], user: "Director Admin", action: "Initiating live template fetch from SC887095 Registry Server", status: "PENDING" },
      ...prev
    ]);
    
    const logs = [
      `[CONNECTION] Established TLS socket channel via CloudSync-02 gateway`,
      `[SECURITY] Authority parsed: Director level credentials approved`,
      `[QUERY] SELECT * FROM template_library WHERE active = true`,
      `[FETCHING] Fetching template manifest (Homepage Hero, Social Promo, bumpers, verticals)...`,
      `[SCHEMA] Parsing 7 multi-scene overlay schema profiles ...`,
      `[COMPLETED] Successfully authenticated and retrieved 7 production templates`
    ];
    
    let i = 0;
    const interval = setInterval(() => {
      if (i < logs.length) {
        setTemplateFetchLogs(prev => [...prev, logs[i]]);
        i++;
      } else {
        clearInterval(interval);
        setIsFetchingTemplates(false);
        setTemplatesHasLoadedOnce(true);
        setAuditLogs(prev => [
          { id: "log-tfc-" + Date.now(), time: new Date().toTimeString().split(' ')[0], user: "Registry Sync", action: "Fetched 7 production templates successfully", status: "SUCCESS" },
          ...prev
        ]);
      }
    }, 400);
  };

  useEffect(() => {
    if (activeSection === 'TEMPLATE_LIBRARY' && !templatesHasLoadedOnce && !isFetchingTemplates) {
      simulateFetchTemplates();
    }
  }, [activeSection]);

  const handleStatusChangeWithSafety = (vi: any, newStatus: string) => {
    // Safety check 1: Empty title check
    if (!vi.title || vi.title.trim() === '') {
      setDuplicateWarning("SAFETY ERROR: Video asset title is invalid or empty.");
      return;
    }
    
    // Safety check 2: Major Brand configuration check
    if (!brandAssets.company) {
      setDuplicateWarning("SAFETY ERROR: Brand configuration missing. Setup company registry name in Assets Manager first.");
      return;
    }
    
    // Safety check 3: Duplication detector for published assets
    const hasOverlap = videosList.some(v => v.id !== vi.id && v.title.toLowerCase().trim() === vi.title.toLowerCase().trim());
    if (hasOverlap && newStatus === 'Published') {
      setDuplicateWarning(`SAFETY HALT: Duplicate title overlap detected. Refuse auto-indexing of duplicate assets to protect organic channel metrics.`);
      return;
    }

    // Safety check 4: Compliance contact checks
    if (!brandAssets.phone && ['Social Media Vertical Video', 'Guides & Videos Walkthrough Clip', 'Social Media Dynamic Clip', 'Outbound Video Campaign'].includes(vi.type)) {
      setDuplicateWarning("SAFETY HALT: Campaign templates require a verified contact telephone number in Brand Assets.");
      return;
    }

    // All safety constraints satisfied -> apply status shift
    setVideosList(videosList.map(v => v.id === vi.id ? { ...v, status: newStatus } : v));
    setDuplicateWarning(null); // Clear warnings
    
    setAuditLogs([
      { 
        id: "log-mut-" + Date.now(), 
        time: new Date().toTimeString().split(' ')[0], 
        user: "Lead Director", 
        action: `Lifecycle event: '${vi.title}' changed to ${newStatus.toUpperCase()} [SAFETY CHECKS: PASSED]`, 
        status: "SUCCESS" 
      },
      ...auditLogs
    ]);
  };

  // Diagnostics running console logging simulation
  const [consoleIsRunning, setConsoleIsRunning] = useState(false);
  const [consoleProgress, setConsoleProgress] = useState(0);
  const [consoleLogs, setConsoleLogs] = useState<string[]>([]);
  const [consoleHeader, setConsoleHeader] = useState("");

  const runConsoleSim = (header: string, logs: string[]) => {
    if (consoleIsRunning || isPublic) return; // Guard for public
    setConsoleIsRunning(true);
    setConsoleProgress(0);
    setConsoleHeader(header);
    setConsoleLogs([`[INITIALIZING] Hooking into core automation scheduler pipeline...`]);
    
    let currentProg = 0;
    const interval = setInterval(() => {
      currentProg += 20;
      setConsoleProgress(currentProg);
      
      const logIdx = Math.floor((currentProg / 100) * logs.length);
      if (logs[logIdx] && !consoleLogs.includes(logs[logIdx])) {
        setConsoleLogs(prev => [...prev, logs[logIdx]]);
      }

      if (currentProg >= 100) {
        clearInterval(interval);
        setConsoleIsRunning(false);
        setConsoleProgress(100);
        setConsoleLogs(prev => [...prev, `[FINISHED] Operation compiled completely inside Nexorax Core Library.`]);
      }
    }, 400);
  };

  // 10 sections navigation matching requested ordered sections
  const subSectionsMenu = [
    { id: 'BRAND_ASSETS', label: '1. Brand Assets', icon: Briefcase, color: 'border-cyan-500/20 text-cyan-400' },
    { id: 'VIDEO_GENERATOR', label: '2. Video Generator', icon: Wand2, color: 'border-indigo-500/20 text-indigo-400' },
    { id: 'VIDEO_PACK', label: '3. Video Pack Gen', icon: Sparkles, color: 'border-purple-500/20 text-purple-400' },
    { id: 'VIDEO_QUEUE', label: '4. Video Queue', icon: CheckSquare, color: 'border-emerald-500/20 text-emerald-400' },
    { id: 'TEMPLATE_LIBRARY', label: '5. Template Library', icon: Layout, color: 'border-amber-500/20 text-amber-400' },
    { id: 'GUIDES_SYNC', label: '6. Guide Videos', icon: Link, color: 'border-blue-500/20 text-blue-400' },
    { id: 'HOMEPAGE_MEDIA', label: '7. Homepage Media', icon: Home, color: 'border-teal-500/20 text-teal-400' },
    { id: 'SOCIAL_QUEUE', label: '8. Social Queue', icon: Share2, color: 'border-pink-500/20 text-pink-400' },
    { id: 'PUBLISH_SCHEDULER', label: '9. Scheduled Checks', icon: Clock, color: 'border-rose-500/20 text-rose-450' },
    { id: 'OVERVIEW_DASHBOARD', label: '10. Overview Hud', icon: BarChart2, color: 'border-cyan-500/30 text-cyan-500' }
  ];

  // Filters navigation elements to secure private internal controls
  const visibleSections = isPublic
    ? subSectionsMenu.filter(sec => ['HOMEPAGE_MEDIA', 'GUIDES_SYNC', 'SOCIAL_QUEUE', 'OVERVIEW_DASHBOARD'].includes(sec.id))
    : subSectionsMenu;

  useEffect(() => {
    if (isPublic && ['BRAND_ASSETS', 'VIDEO_GENERATOR', 'VIDEO_PACK', 'VIDEO_QUEUE', 'TEMPLATE_LIBRARY', 'PUBLISH_SCHEDULER'].includes(activeSection)) {
      setActiveSection('OVERVIEW_DASHBOARD');
    }
  }, [isPublic, activeSection]);

  return (
    <div className="space-y-6">

      {/* Dynamic Role Access Switcher for Safety Testing & Public View */}
      <div className="bg-gradient-to-r from-purple-950/20 to-black/80 border border-purple-500/30 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded bg-purple-500/10 border border-purple-500/20 text-purple-400 shrink-0">
            <Shield className="w-5 h-5 text-purple-400 animate-pulse" />
          </div>
          <div>
            <h4 className="text-white text-xs font-mono uppercase tracking-widest font-black flex items-center gap-1.5">
              Session Security Node Access <span className="text-[6.5px] bg-purple-500/10 border border-purple-500/30 px-1 py-0.5 rounded text-purple-300">PREVIEW MODULATOR</span>
            </h4>
            <p className="text-slate-400 text-[10px] mt-0.5 uppercase tracking-wide leading-relaxed">
              Toggle between Public View (safety-compliant, isolated) and Enterprise Admin mode dynamically.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className="font-mono text-[9px] text-slate-500 uppercase font-bold">MODE ACCESS:</span>
          <div className="grid grid-cols-2 bg-black/60 border border-slate-800 rounded-lg p-1">
            <button
              onClick={() => {
                if (setSessionRoleInParent) {
                  setSessionRoleInParent('PUBLIC');
                } else {
                  setLocalSessionRole('PUBLIC');
                }
              }}
              className={`px-3 py-1.5 rounded-md font-mono text-[9px] uppercase font-black transition-all ${
                currentRole === 'PUBLIC' 
                  ? 'bg-purple-600/90 text-white shadow-[0_0_8px_rgba(168,85,247,0.4)] font-bold' 
                  : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              Public View
            </button>
            <button
              onClick={() => {
                if (setSessionRoleInParent) {
                  setSessionRoleInParent('ADMIN');
                } else {
                  setLocalSessionRole('ADMIN');
                }
              }}
              className={`px-3 py-1.5 rounded-md font-mono text-[9px] uppercase font-black transition-all ${
                currentRole !== 'PUBLIC' 
                  ? 'bg-cyan-500 text-black shadow-[0_0_8px_rgba(6,182,212,0.4)] font-black' 
                  : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              Director Admin
            </button>
          </div>
        </div>
      </div>
      
      {/* 10-SECTION CONTROL PANEL GRID LAYOUT */}
      <div className="bg-[#04060d]/90 border border-slate-800 rounded-2xl p-4.5 backdrop-blur-md relative overflow-hidden">
        {/* Subtle grid accent background */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#111119_1px,transparent_1px),linear-gradient(to_bottom,#111119_1px,transparent_1px)] bg-[size:24px_24px] opacity-20 pointer-events-none" />
        
        <div className="relative z-10">
          <span className="font-mono text-[9px] text-purple-400 tracking-[0.2em] uppercase font-black block mb-2.5 flex items-center justify-between">
            <span>// DYNAMIC AUTOMATION MATRIX // {visibleSections.length} CHANNELS ACCESSIBLE</span>
            <span className="text-slate-500 text-[8px] font-normal tracking-wide">ROLE: {currentRole}</span>
          </span>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {visibleSections.map(sec => {
              const Icon = sec.icon;
              const isActive = activeSection === sec.id;
              return (
                <button
                  key={sec.id}
                  onClick={() => {
                    setActiveSection(sec.id);
                    setDuplicateWarning(null);
                  }}
                  className={`p-3.5 rounded-xl border flex flex-col items-center justify-center text-center transition-all duration-300 relative group overflow-hidden ${isActive ? 'bg-gradient-to-b from-purple-950/20 to-black/60 border-purple-500 text-white shadow-[0_0_15px_rgba(139,92,246,0.2)]' : 'border-slate-800 bg-black/40 text-slate-400 hover:text-slate-100 hover:border-slate-700 hover:bg-slate-900/40'}`}
                >
                  <Icon className={`w-4 h-4 mb-2 ${isActive ? 'text-purple-400 animate-pulse' : 'text-slate-500 group-hover:text-purple-300'}`} />
                  <span className="font-sans font-extrabold tracking-tight text-[10.5px] uppercase">{sec.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* DYNAMIC SECTION CONTENTS DISPLAY */}
      
      {/* 1. BRAND ASSETS MANAGER CLIENT NODE */}
      {activeSection === 'BRAND_ASSETS' && (
        <div className="bg-black/40 border border-slate-800 rounded-2xl p-6 backdrop-blur-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-slate-800 pb-4 mb-6">
            <div>
              <h3 className="text-white text-md font-sans font-extrabold uppercase tracking-widest flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-cyan-400" /> 1. BRAND ASSETS MANAGER (SC887095)
              </h3>
              <p className="text-slate-400 text-xs mt-1">
                Configure corporate identity values, billing anchors, and public contact numbers dynamically compiled inside outbound video overlays.
              </p>
            </div>
            <button
              onClick={() => {
                const newLog = {
                  id: "log-" + Date.now(),
                  time: new Date().toTimeString().split(' ')[0],
                  user: "Lead Director",
                  action: "Mirrored customized brand profiles to Google Drive library (CloudSync-02)",
                  status: "SUCCESS"
                };
                setAuditLogs([newLog, ...auditLogs]);
                setDuplicateWarning("SUCCESS: Central brand profile variables compiled, validated, and synced to sitemaps completely.");
              }}
              className="mt-3 md:mt-0 bg-cyan-500 hover:bg-cyan-400 text-black px-4.5 py-2.5 rounded-lg font-mono text-xs font-bold tracking-widest transition-all uppercase shadow-[0_0_15px_rgba(6,182,212,0.4)]"
            >
              SAVE & SYNC BRAND ASSETS
            </button>
          </div>

          {duplicateWarning && (
            <div className="p-3.5 rounded-xl border font-mono text-[9.5px] mb-6 bg-emerald-950/20 border-emerald-500/40 text-emerald-400">
              {duplicateWarning}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { label: "Company Registry Name", key: "company", placeholder: "e.g., NexoraX Digital" },
              { label: "Corporate Tagline", key: "tagline", placeholder: "Build Smarter. Scale Faster." },
              { label: "Compliance Public Contact Number", key: "phone", placeholder: "e.g., 08000 622827" },
              { label: "General Mandate Mailbox", key: "primaryEmail", placeholder: "info@nexoraxdigital.co.uk" },
              { label: "Enterprise Support Ticketbox", key: "supportEmail", placeholder: "support@nexoraxdigital.co.uk" },
              { label: "Central Web Domain", key: "websiteUrl", placeholder: "nexoraxdigital.co.uk" },
              { label: "Outbound Social Target Handle", key: "socialHandles", placeholder: "@nexoraxdigital" },
              { label: "Voiceover Accent Acoustic Preset", key: "voiceoverTone", placeholder: "e.g., Sincere British Corporate Leader" }
            ].map(field => (
              <div key={field.key} className="flex flex-col space-y-1.5 focus-within:text-purple-300">
                <label className="text-[9px] font-mono uppercase tracking-wider text-slate-400 font-bold">{field.label}</label>
                <input
                  type="text"
                  className="bg-[#020306] border border-slate-800 focus:border-cyan-500/50 rounded-lg py-2.5 px-3 font-mono text-xs text-white focus:outline-none transition-colors"
                  value={brandAssets[field.key as keyof typeof brandAssets] as string}
                  placeholder={field.placeholder}
                  onChange={(e) => {
                    setBrandAssets({ ...brandAssets, [field.key]: e.target.value });
                  }}
                />
              </div>
            ))}
            
            <div className="flex flex-col space-y-1.5">
              <label className="text-[9px] font-mono uppercase tracking-wider text-slate-400 font-bold">Theme Primary Aesthetic</label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { label: "Obsidian", color: brandAssets.themePrimary },
                  { label: "Cyan", color: brandAssets.themeAccent1 },
                  { label: "Purple", color: brandAssets.themeAccent2 },
                  { label: "Gold", color: brandAssets.themeHighlight }
                ].map((col, cidx) => (
                  <div key={cidx} className="flex flex-col items-center gap-1 bg-[#020306] border border-slate-800 p-1.5 rounded-lg text-center">
                    <div className="w-5 h-5 rounded" style={{ backgroundColor: col.color }} />
                    <span className="text-[7.5px] font-mono text-slate-500 uppercase">{col.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col space-y-1.5 md:col-span-2">
              <label className="text-[9px] font-mono uppercase tracking-wider text-slate-400 font-bold">Default Outro CTA Matrix Value</label>
              <input
                type="text"
                className="bg-[#020306] border border-[#1e293b] focus:border-cyan-500/50 rounded-lg py-2.5 px-3 font-mono text-xs text-white focus:outline-none"
                value={brandAssets.ctaText}
                onChange={(e) => setBrandAssets({ ...brandAssets, ctaText: e.target.value })}
              />
            </div>

            <div className="flex flex-col space-y-1.5 md:col-span-3">
              <label className="text-[9px] font-mono uppercase tracking-wider text-slate-400 font-bold">Central Corporate Footer Disclosures & Audits (NI: NH448796B)</label>
              <textarea
                rows={2}
                className="bg-[#020306] border border-slate-800 focus:border-cyan-500/50 rounded-lg py-2 px-3 font-mono text-xs text-white focus:outline-none resize-none"
                value={brandAssets.footerDisclosure}
                onChange={(e) => setBrandAssets({ ...brandAssets, footerDisclosure: e.target.value })}
              />
            </div>
          </div>

          <div className="bg-purple-950/20 border border-purple-500/30 rounded-xl p-4 mt-6 text-[9.5px] font-mono flex items-start gap-3">
            <Shield className="w-5 h-5 text-purple-400 shrink-0 mt-0.5" />
            <div className="text-slate-400 space-y-1 leading-relaxed">
              <span className="font-extrabold text-purple-400 uppercase tracking-wider block">FINANCIAL OVERSEER AUDIT PROTOCOL:</span>
              <span>All assets bound above inherit absolute zero-win-no-fee compliance parameters under SC887095 records. Inbound campaign variables sync every Wednesday automatically.</span>
            </div>
          </div>
        </div>
      )}

      {/* 2. VIDEO GENERATOR INTEGRATED FORM */}
      {activeSection === 'VIDEO_GENERATOR' && (
        <div className="bg-black/40 border border-slate-800 rounded-2xl p-6 backdrop-blur-sm">
          <div className="border-b border-slate-800 pb-4 mb-6">
            <h3 className="text-white text-md font-sans font-extrabold uppercase tracking-widest flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-cyan-400 animate-pulse" /> 2. VIDEO GENERATOR CO-ORDINATE ENGINE
            </h3>
            <p className="text-slate-400 text-xs mt-1">
              Construct high-precision promotional videos specifying layout parameters, aspect ratios, subtitles overlays, and kinetic prompts.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            {/* FORM INPUT FIELDS */}
            <div className="lg:col-span-2 bg-slate-950/40 border border-slate-850 p-5 rounded-xl space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col space-y-1">
                  <label className="text-[8.5px] font-mono uppercase tracking-wider text-slate-400 font-bold">1. Video Project Title *</label>
                  <input
                    type="text"
                    className="bg-[#020306] border border-slate-800 focus:border-cyan-500/50 rounded-lg py-2 px-3 text-xs text-white uppercase font-mono"
                    value={formVideoTitle || ""}
                    onChange={(e) => setFormVideoTitle(e.target.value)}
                  />
                </div>

                <div className="flex flex-col space-y-1">
                  <label className="text-[8.5px] font-mono uppercase tracking-wider text-slate-400 font-bold">2. Video Format Type *</label>
                  <select
                    className="bg-[#020306] border border-slate-800 focus:border-cyan-500/55 rounded-lg py-2 px-2 text-xs text-slate-250 font-mono"
                    value={formVideoType}
                    onChange={(e) => setFormVideoType(e.target.value)}
                  >
                    <option value="Homepage Hero Video">Homepage Hero Video</option>
                    <option value="15-second promo video">15-Second Promo Video</option>
                    <option value="35-second promo video">35-Second Executive Promo</option>
                    <option value="Social Media Vertical Video">Social Media Vertical Video</option>
                    <option value="Transition / Bumper Clip">Transition / Bumper Outro</option>
                    <option value="Explainer tutorial walkthrough">Explainer Tutorial Walkthrough</option>
                  </select>
                </div>

                <div className="flex flex-col space-y-1">
                  <label className="text-[8.5px] font-mono uppercase tracking-wider text-slate-400 font-bold">3. Core Target Audience</label>
                  <input
                    type="text"
                    className="bg-[#020306] border border-slate-800 rounded-lg py-2 px-3 text-xs text-white font-mono"
                    value={formTargetAudience || ""}
                    onChange={(e) => setFormTargetAudience(e.target.value)}
                  />
                </div>

                <div className="flex flex-col space-y-1">
                  <label className="text-[8.5px] font-mono uppercase tracking-wider text-slate-400 font-bold">4. Highlighted Product / Service</label>
                  <input
                    type="text"
                    className="bg-[#020306] border border-slate-800 rounded-lg py-2 px-3 text-xs text-white font-mono"
                    value={formServiceOrProduct || ""}
                    onChange={(e) => setFormServiceOrProduct(e.target.value)}
                  />
                </div>

                <div className="flex flex-col space-y-1">
                  <label className="text-[8.5px] font-mono uppercase tracking-wider text-slate-400 font-bold">5. Ideal Sizing / Aspect Ratio</label>
                  <select
                    className="bg-[#020306] border border-slate-800 rounded-lg py-2 px-2 text-xs text-slate-300 font-mono"
                    value={formAspectRatio}
                    onChange={(e) => setFormAspectRatio(e.target.value)}
                  >
                    <option value="16:9">16:9 (Horizontal)</option>
                    <option value="9:16">9:16 (Vertical Reel)</option>
                    <option value="1:1">1:1 (Square Feed)</option>
                  </select>
                </div>

                <div className="flex flex-col space-y-1">
                  <label className="text-[8.5px] font-mono uppercase tracking-wider text-slate-400 font-bold">6. Outro CTA Text</label>
                  <input
                    type="text"
                    className="bg-[#020306] border border-slate-800 rounded-lg py-2 px-3 text-xs text-white font-mono"
                    value={formCta || ""}
                    onChange={(e) => setFormCta(e.target.value)}
                  />
                </div>
              </div>

              {/* Toggles */}
              <div className="p-3.5 bg-black/50 border border-slate-900 rounded-lg space-y-2">
                <span className="font-mono text-[8.5px] text-cyan-400 uppercase tracking-widest font-black block mb-2">CORPORATE OVERLAYS SCHEMAS (FIELDS 7-12)</span>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: "Logo Watermark Overlay", val: formUseLogo, set: setFormUseLogo },
                    { label: `Direct Public Contact Overlay (${brandAssets.phone})`, val: formUsePhone, set: setFormUsePhone },
                    { label: "Text-mono Subtitles Subbed", val: formUseSubtitles, set: setFormUseSubtitles },
                    { label: "Active Speech Synthesis Audio Tracker", val: formUseVoiceover, set: setFormUseVoiceover }
                  ].map((tg, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-zinc-950 p-2.5 rounded border border-slate-850">
                      <span className="font-mono text-[7.5px] uppercase text-slate-400">{tg.label}</span>
                      <input
                        type="checkbox"
                        checked={tg.val}
                        onChange={(e) => tg.set(e.target.checked)}
                        className="w-3.5 h-3.5 text-cyan-500 rounded focus:ring-0 bg-transparent border-slate-800"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col space-y-1">
                <label className="text-[8.5px] font-mono uppercase tracking-wider text-slate-400 font-bold">13. Kinetic Camera Motion & Prompt Structure *</label>
                <textarea
                  rows={2}
                  className="bg-[#020306] border border-slate-800 focus:border-cyan-500/50 rounded-lg p-2.5 text-xs text-slate-200 font-mono resize-none focus:outline-none"
                  value={formScenePrompt || ""}
                  onChange={(e) => setFormScenePrompt(e.target.value)}
                  placeholder="e.g. Deep space orbits flying past glowing cyan data server racks..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col space-y-1 pb-1">
                  <label className="text-[8.5px] font-mono uppercase tracking-wider text-slate-400 font-bold">14. Run Priority Speed</label>
                  <select
                    className="bg-[#020306] border border-slate-800 rounded-lg py-2 px-2 text-xs text-white font-mono"
                    value={formPriority}
                    onChange={(e) => setFormPriority(e.target.value)}
                  >
                    <option value="Critical">Wednesday Critical Queue</option>
                    <option value="High">Priority High</option>
                    <option value="Medium">Medium Stock</option>
                  </select>
                </div>

                <div className="flex flex-col space-y-1">
                  <label className="text-[8.5px] font-mono uppercase tracking-wider text-slate-400 font-bold">15. Content Duplicate Check</label>
                  <button
                    onClick={triggerVerifyDuplicate}
                    className="bg-purple-950/20 border border-purple-500/30 hover:bg-purple-900/30 text-purple-400 py-2 rounded-lg text-xs font-mono font-bold uppercase tracking-wider transition-all"
                  >
                    RUN CONTENT DUPLICATE CHECK
                  </button>
                </div>
              </div>
            </div>

            {/* ACTION DIRECTIVE PANEL */}
            <div className="flex flex-col space-y-4">
              <div className="bg-[#04060d] border border-slate-800 rounded-xl p-4 flex flex-col space-y-3.5">
                <h4 className="text-white text-[11px] font-mono uppercase tracking-wider font-extrabold flex items-center gap-1.5 border-b border-slate-850 pb-2">
                  <SlidersIcon className="w-4 h-4 text-cyan-400" /> FACTORY EXECUTIONS
                </h4>

                <button
                  disabled={consoleIsRunning}
                  onClick={() => {
                    if (!formVideoTitle) {
                      setDuplicateWarning("ERROR: Video project title is required to declare on catalog.");
                      return;
                    }
                    runConsoleSim(`RENDERING INDIVIDUAL VIDEO OUTLAY: ${formVideoTitle.toUpperCase()}`, [
                      `[STAGE 1] Checking duplicates for target title: ${formVideoTitle}`,
                      `[STAGE 2] Synthesizing speech timeline accent characteristics: "${brandAssets.voiceoverTone}"`,
                      `[STAGE 3] Multiplexing subtitle tracks under duration: ${formDuration}`
                    ]);

                    // Add simulated video
                    const newId = "auto-vid-" + Date.now();
                    const newVid = {
                      id: newId,
                      title: formVideoTitle,
                      type: formVideoType,
                      goal: formVideoGoal || "Expand brand outreach",
                      targetAudience: formTargetAudience || "SMEs",
                      serviceOrProduct: formServiceOrProduct || "SaaS Matrix",
                      duration: formDuration || "15 seconds",
                      aspectRatio: formAspectRatio,
                      tone: formTone || "Cinematic",
                      cta: formCta || "Call " + brandAssets.phone,
                      useLogo: formUseLogo,
                      useWebsite: formUseWebsite,
                      usePhone: formUsePhone,
                      useEmail: formUseEmail,
                      useVoiceover: formUseVoiceover,
                      useSubtitles: formUseSubtitles,
                      useThumbnail: formUseThumbnail,
                      scenePrompt: formScenePrompt,
                      notes: formNotes,
                      priority: formPriority,
                      status: "Needs Review",
                      pagePlacement: "Distribution Library",
                      category: "ad-creative",
                      views: 0,
                      createdAt: new Date().toISOString().replace('T', ' ').substring(0, 16),
                      script: { hook: "Stop manual scaling!", problem: "Inbound lag", solution: "NexoraX system", benefit: "Boost speed by 10x", cta: brandAssets.phone },
                      voiceoverScript: `Attention ${formTargetAudience || "everyone"}: Boost your service instantly. Dial ${brandAssets.phone}.`,
                      subtitleText: "0.8s NEURAL STYLES DETECTED // HIGH RENDER",
                      thumbnailText: formVideoTitle.toUpperCase(),
                      thumbnailPrompt: formScenePrompt,
                      suggestedFilename: `${formVideoTitle.toLowerCase().replace(/\s+/g, '_')}.mp4`,
                      videoUrl: "/brain1.mp4"
                    };

                    setVideosList([newVid, ...videosList]);
                    setAuditLogs([
                      { id: "log-gen-" + Date.now(), time: new Date().toTimeString().split(' ')[0], user: "Factory Node", action: `Queued and created custom video: "${formVideoTitle}"`, status: "SUCCESS" },
                      ...auditLogs
                    ]);
                  }}
                  className="w-full bg-cyan-500 hover:bg-cyan-400 text-black py-3 rounded-xl font-mono text-xs font-black uppercase tracking-widest transition-all shadow-[0_0_12px_rgba(6,182,212,0.3)] disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  {consoleIsRunning ? "PROCESSING MOVEMENT..." : "BUILD & QUEUE INDIVIDUAL"}
                </button>

                <div className="border border-slate-900 bg-black/50 p-3.5 rounded-lg text-slate-400 leading-relaxed font-mono text-[8px] uppercase">
                  <span className="font-bold text-amber-500 block mb-1">AUTOMATED DIRECTIVES:</span>
                  <span>When clicking "BUILD", the matrix automatically scans current titles to prevent SEO penalties and schedules the newly built asset to the distribution lists.</span>
                </div>
              </div>

              {consoleIsRunning && (
                <div className="bg-black border border-cyan-500/20 rounded-xl p-4 font-mono text-[9px] text-cyan-400 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-extrabold uppercase animate-pulse">{consoleHeader}</span>
                    <span>{consoleProgress}%</span>
                  </div>
                  <div className="w-full bg-slate-900 h-1.5 rounded-full overflow-hidden">
                    <div className="bg-cyan-400 h-full transition-all duration-300" style={{ width: `${consoleProgress}%` }} />
                  </div>
                  <div className="space-y-1 text-slate-500 pt-1">
                    {consoleLogs.map((lg, idx) => (
                      <div key={idx} className="line-clamp-1">&#9658; {lg}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 3. VIDEO PACK GENERATOR (MULTI-VIDEO SUITE) */}
      {activeSection === 'VIDEO_PACK' && (
        <div className="bg-[#04060d]/90 border border-slate-800 rounded-2xl p-6 backdrop-blur-md relative overflow-hidden">
          <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-slate-850 pb-4 mb-6">
            <div>
              <h3 className="text-white text-md font-sans font-extrabold uppercase tracking-widest flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-400 animate-pulse" /> 3. ZERO-BUDGET CAMPAIGN VIDEO PACK
              </h3>
              <p className="text-slate-400 text-xs mt-1">
                Trigger high-velocity Zero-Budget Campaign Video Pack. Create 4 vital promotional assets with 1-click execution.
              </p>
            </div>
            
            <button
              disabled={consoleIsRunning}
              onClick={() => {
                const seedName = formVideoTitle || "Enterprise Multi Pack";
                handleRunAutomationRender(seedName, true);
              }}
              className="mt-3 md:mt-0 bg-purple-600 hover:bg-purple-500 active:bg-purple-700 text-white px-5 py-3 rounded-xl font-mono text-xs font-black tracking-widest transition-all uppercase shadow-[0_0_15px_rgba(139,92,246,0.5)] animate-pulse disabled:opacity-30"
            >
              COMPILE ZERO-BUDGET CAMPAIGN VIDEO PACK
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <h4 className="text-slate-300 font-mono text-[9px] tracking-wider uppercase font-black">// INCLUDED VIDEO SUITE ASSETS</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { title: "16:9 Looping Homepage Hero", ratio: "16:9 Landscape", time: "15-Sec loopable bg", desc: "Crafted as ambient background loop showcasing glass cubes spinning and gold wireframes highlight." },
                  { title: "15s High Conversion Promo", ratio: "16:9 Horizontal", time: "15s exact", desc: "Outbound clip featuring active public contact overlays, lower third title tags, and standard speech synthetic audio." },
                  { title: "9:16 Portrait Social Reel", ratio: "9:16 Portrait vertical", time: "15s portrait", desc: "Engineered with attention grabber subtitles and vertical layout margins optimized for mobile TikTok & Shorts." },
                  { title: "4s Logo Transition Bumper", ratio: "16:9 Landscape", time: "4s sting", desc: "High-contrast visual sting rendering logo reverse implosion particle sweeps for outro bumpers." }
                ].map((item, idx) => (
                  <div key={idx} className="bg-black/50 border border-[#1e293b] p-4 rounded-xl flex flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-white text-xs font-bold">{item.title}</span>
                        <span className="text-[7.5px] font-mono text-purple-400 bg-purple-950/30 border border-purple-500/20 px-1.5 py-0.5 rounded uppercase">{item.ratio}</span>
                      </div>
                      <span className="text-[8px] font-mono text-slate-500 block mb-2">{item.time}</span>
                      <p className="font-mono text-[8px] leading-relaxed text-slate-400 uppercase">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-black/40 border border-slate-800 p-4.5 rounded-xl flex flex-col justify-between">
              <div>
                <span className="font-mono text-[9px] text-purple-400 uppercase tracking-widest block mb-2 font-black">// PACK BENEFITS</span>
                <p className="font-mono text-[8.5px] leading-relaxed text-slate-300 uppercase mb-4">
                  Using 1-click suite rendering bypasses default single compilation queues. Output matrices automatically register to corresponding placements inside distribution portals and Wednesday ledger audits.
                </p>
                <div className="p-3 bg-slate-900/40 rounded border border-slate-850 font-mono text-[7px] space-y-1.5 uppercase text-slate-500">
                  <div className="flex justify-between"><span>AUTOMATED SLITS:</span> <span className="text-white">TRUE</span></div>
                  <div className="flex justify-between"><span>ZERO COST REMUNERATION:</span> <span className="text-white">NI: NH448796B</span></div>
                  <div className="flex justify-between"><span>GOOGLE DRIVE MIRROR:</span> <span className="text-white font-bold text-cyan-400">CLOUDSYNC-02</span></div>
                </div>
              </div>
              
              {consoleIsRunning && (
                <div className="mt-4 bg-purple-950/20 border border-purple-500/25 p-3 rounded font-mono text-[8px] text-purple-300 animate-pulse">
                  Rendering Multi-Video Pack Suite. Compiling structures dynamically... ({consoleProgress}%)
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 4. VIDEO QUEUE / STATUS PIPELINE */}
      {activeSection === 'VIDEO_QUEUE' && (
        <div className="space-y-6">
          {/* Draggable stages status */}
          <div className="bg-black/50 border border-slate-800 rounded-2xl p-5 backdrop-blur-sm">
            <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-zinc-800 pb-3 mb-5">
              <h4 className="text-white text-xs font-mono uppercase tracking-widest font-black flex items-center gap-2">
                <Activity className="w-4 h-4 text-cyan-400 animate-pulse" /> 4. VIDEO PIPELINE QUEUE (8 STAGES)
              </h4>
              <button
                onClick={() => {
                  setVideosList(videosList.map(v => v.status === 'Needs Review' ? { ...v, status: 'Approved' } : v));
                  setDuplicateWarning("SUCCESS: All review queues approved completely.");
                  setAuditLogs([
                    { id: "log-app-" + Date.now(), time: new Date().toTimeString().split(' ')[0], user: "Director Admin", action: "Bulk human review: Approved all pending review segments.", status: "SUCCESS" },
                    ...auditLogs
                  ]);
                }}
                className="mt-2 md:mt-0 bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-900/40 text-emerald-400 px-3 py-1.5 rounded uppercase font-bold text-[9px] tracking-widest font-mono"
              >
                BULK APPROVE PENDING REVIEW
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 lg:grid-cols-9 gap-3 max-h-[460px] overflow-y-auto w-full">
              {[
                { id: "Draft", label: "DRAFT SOURCE", col: "border-slate-800 text-slate-400 bg-slate-900/5" },
                { id: "Script Ready", label: "SCRIPT COMPILE", col: "border-purple-900/40 text-purple-400 bg-purple-950/5" },
                { id: "Waiting for Render", label: "AWAIT RENDER", col: "border-blue-900/40 text-blue-400 bg-blue-950/5" },
                { id: "Rendering", label: "RENDERING", col: "border-cyan-500/30 text-cyan-400 bg-cyan-950/10" },
                { id: "Needs Review", label: "MANAGER REVIEW", col: "border-amber-500/30 text-amber-400 bg-amber-500/5 animate-pulse" },
                { id: "Approved", label: "APPROVED", col: "border-emerald-500/30 text-emerald-400 bg-emerald-500/5" },
                { id: "Published", label: "PUBLISHED", col: "border-teal-500/40 text-teal-300 bg-teal-500/5 font-black" },
                { id: "Archived", label: "ARCHIVED", col: "border-zinc-800 text-zinc-500 bg-zinc-900/10" },
                { id: "Failed", label: "FAILED HALT", col: "border-rose-900/40 text-rose-400 bg-rose-955/5" }
              ].map(step => {
                const stepItems = videosList.filter(v => v.status === step.id);
                return (
                  <div key={step.id} className={`p-2 rounded-lg border flex flex-col space-y-2 h-[340px] overflow-hidden ${step.col}`}>
                    <div className="flex items-center justify-between border-b border-slate-800/40 pb-1">
                      <span className="font-mono text-[7px] font-extrabold uppercase truncate tracking-tighter">{step.label}</span>
                      <span className="text-[9px] font-mono font-bold">({stepItems.length})</span>
                    </div>

                    <div className="flex-1 overflow-y-auto space-y-1.5 pr-0.5">
                      {stepItems.length === 0 ? (
                        <div className="font-mono text-[6px] text-slate-600 text-center uppercase py-8">0 items</div>
                      ) : (
                        stepItems.map(vi => (
                          <div 
                            key={vi.id} 
                            onClick={() => {
                              setActiveVideoObject(vi);
                              setFormVideoTitle(vi.title);
                              setFormVideoType(vi.type);
                              setFormScenePrompt(vi.scenePrompt);
                            }}
                            className={`p-2 rounded border cursor-pointer hover:border-cyan-500/50 transition-colors bg-black/60 flex flex-col space-y-1 ${activeVideoObject?.id === vi.id ? 'border-cyan-500 ring-1 ring-cyan-500/20' : 'border-slate-850'}`}
                          >
                            <span className="font-sans font-bold text-[8.5px] text-white line-clamp-1 truncate">{vi.title}</span>
                            <span className="font-mono text-[7px] text-slate-500 select-all">{vi.id}</span>
                            
                            <div className="flex items-center justify-between mt-1 pt-1 border-t border-slate-800/40">
                              <span className="font-mono text-[6.5px] text-cyan-500 font-extrabold">{vi.duration}</span>
                              <span className="font-mono text-[6.5px] text-slate-500">{vi.aspectRatio}</span>
                            </div>
                            <div className="flex flex-wrap gap-1 justify-end mt-1 font-mono text-[6px] w-full">
                              {vi.status === 'Needs Review' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusChangeWithSafety(vi, 'Approved');
                                  }}
                                  className="bg-emerald-500/25 text-emerald-400 font-bold px-1 py-0.5 rounded border border-emerald-500/30 uppercase cursor-pointer hover:bg-emerald-500/40 text-[6.5px]"
                                >
                                  APPROVE
                                </button>
                              )}
                              
                              {vi.status !== 'Published' && vi.status !== 'Archived' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusChangeWithSafety(vi, 'Published');
                                  }}
                                  className="bg-teal-500 hover:bg-teal-400 text-black px-1 py-0.5 rounded uppercase tracking-tighter block text-[6.5px] font-black cursor-pointer"
                                >
                                  PUBLISH
                                </button>
                              )}

                              {vi.status !== 'Archived' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusChangeWithSafety(vi, 'Archived');
                                  }}
                                  className="bg-zinc-800 text-zinc-300 border border-zinc-700 hover:bg-zinc-700 px-1 py-0.5 rounded uppercase block text-[6.5px] cursor-pointer"
                                >
                                  ARCHIVE
                                </button>
                              )}

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setVideosList(videosList.filter(v => v.id !== vi.id));
                                  if (activeVideoObject?.id === vi.id) setActiveVideoObject(null);
                                }}
                                className="text-rose-500 hover:text-rose-400 px-1 hover:bg-rose-950/20 rounded cursor-pointer text-[6.5px] font-bold"
                              >
                                DELETE
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Detailed output directory inspector */}
          {activeVideoObject && (
            <div className="bg-black/60 border border-slate-800 rounded-2xl p-5 backdrop-blur-md">
              <h4 className="text-white text-xs font-mono uppercase tracking-[0.2em] font-black border-b border-slate-800 pb-3 mb-5 flex items-center gap-2">
                <FileText className="w-4 h-4 text-cyan-400" /> OUTPUT CONSOLE INSPECTOR: {activeVideoObject.title}
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-4 bg-[#020306] rounded-xl border border-slate-850 flex flex-col space-y-2">
                  <span className="font-mono text-[9px] text-cyan-400 uppercase tracking-widest font-black block border-b border-slate-800 pb-1.5">PROMO SCRIPT DOCUMENTATION</span>
                  {activeVideoObject.script ? (
                    <div className="space-y-2.5 font-mono text-[8.5px] text-slate-400 leading-relaxed uppercase">
                      <div><strong className="text-slate-200 block">[HANGER HOOK]:</strong> {activeVideoObject.script.hook}</div>
                      <div><strong className="text-slate-200 block">[SYSTEM PROBLEM]:</strong> {activeVideoObject.script.problem}</div>
                      <div><strong className="text-slate-200 block">[AI RESOLUTION]:</strong> {activeVideoObject.script.solution}</div>
                      <div><strong className="text-slate-200 block">[PROFIT BENEFIT]:</strong> {activeVideoObject.script.benefit}</div>
                      <div><strong className="text-slate-200 block">[CALL DIRECT]:</strong> {activeVideoObject.script.cta}</div>
                    </div>
                  ) : (
                    <span className="font-mono text-[8px] text-slate-600">NO DIALOGUE REGISTERED FOR THE SELECTED COMPONENT.</span>
                  )}
                </div>

                <div className="p-4 bg-[#020306] rounded-xl border border-slate-850 flex flex-col space-y-2">
                  <span className="font-mono text-[9px] text-purple-400 uppercase tracking-widest font-black block border-b border-slate-800 pb-1.5">VOICEOVER DIALOG DIALOGUE ({brandAssets.voiceoverTone})</span>
                  <p className="font-mono text-[9.5px] text-white italic leading-relaxed">
                    {activeVideoObject.voiceoverScript || "[AMBIENT LOOP COMPONENT - ACOUSTIC VOICEOVER DISABLED]"}
                  </p>
                  <div className="bg-slate-800/10 border border-slate-850 p-2 rounded text-[7.5px] font-mono text-slate-400 mt-auto uppercase">
                    &#9656; Synthesized dialog matching Wednesday billing clearance guidelines (SC887095)
                  </div>
                </div>

                <div className="p-4 bg-[#020306] rounded-xl border border-slate-850 flex flex-col space-y-3">
                  <div>
                    <span className="font-mono text-[9px] text-amber-400 uppercase tracking-widest font-black block border-b border-slate-800 pb-1.5">SUBTITLE TIMELINES (SRT FORMAT)</span>
                    <p className="font-mono text-[8px] text-slate-300 whitespace-pre-line leading-relaxed italic mt-1.5">
                      {activeVideoObject.subtitleText ? `00:00:01,000 --> 00:00:03,500\n${activeVideoObject.title.toUpperCase()}\n\n00:00:04,100 --> 00:00:12,000\n${activeVideoObject.subtitleText}` : "[AMBIENT LOOP CAPTIONS PREEMPTED]"}
                    </p>
                  </div>
                  
                  {activeVideoObject.thumbnailPrompt && (
                    <div className="border-t border-slate-800/60 pt-3">
                      <span className="font-mono text-[8.5px] text-cyan-400 uppercase tracking-widest font-black block pb-1 border-b border-slate-850">THUMBNAIL IMAGE PROMPT</span>
                      <p className="font-mono text-[8px] text-slate-500 italic mt-1">
                        "{activeVideoObject.thumbnailPrompt}"
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {/* 5. TEMPLATE LIBRARY PRESETS */}
      {activeSection === 'TEMPLATE_LIBRARY' && (
        <div className="bg-black/40 border border-slate-800 rounded-2xl p-6 backdrop-blur-sm">
          <div className="border-b border-slate-800 pb-4 mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h3 className="text-white text-md font-sans font-extrabold uppercase tracking-widest flex items-center gap-2">
                <Layout className="w-5 h-5 text-amber-400" /> 5. REUSABLE AUTOMATION TEMPLATE LIBRARY
              </h3>
              <p className="text-slate-400 text-xs mt-1">
                Select key pre-built template cards to preset values in the Video Generator node instantly, ensuring consistent campaign brand dimensions.
              </p>
            </div>
            
            <div className="text-[9px] font-mono text-slate-500 uppercase flex items-center gap-2 bg-slate-900/40 px-3 py-1.5 rounded-lg border border-slate-850">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span>REGISTRY REGION: SC887095-NODE-02</span>
            </div>
          </div>

          {/* SEARCH & FORMAT FILTER COMPONENT */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-[#020306] border border-slate-900 p-4 rounded-xl mb-6">
            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
              <div className="relative flex-1 md:flex-initial">
                <input
                  type="text"
                  placeholder="Search templates (e.g. Hero, Promo)..."
                  value={templateSearchQuery}
                  onChange={(e) => setTemplateSearchQuery(e.target.value)}
                  className="bg-black/40 border border-slate-800 rounded-lg px-3 py-1.5 text-xs text-white placeholder-slate-600 font-mono focus:outline-none focus:border-cyan-500 w-full md:w-64"
                />
              </div>
              <select
                value={templateFilterRatio}
                onChange={(e) => setTemplateFilterRatio(e.target.value)}
                className="bg-black/40 border border-slate-800 rounded-lg px-3 py-1.5 text-xs text-white font-mono focus:outline-none focus:border-cyan-500 cursor-pointer"
              >
                <option value="ALL">All Aspect Ratios</option>
                <option value="16:9">Landscape (16:9)</option>
                <option value="9:16">Vertical (9:16)</option>
              </select>
            </div>

            <button
              onClick={simulateFetchTemplates}
              disabled={isFetchingTemplates}
              className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 disabled:opacity-50 text-black font-mono text-[9px] font-black uppercase px-4 py-2 rounded-lg cursor-pointer transition-all shrink-0 shadow-[0_0_15px_rgba(6,182,212,0.15)]"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isFetchingTemplates ? 'animate-spin' : ''}`} />
              {isFetchingTemplates ? "SYNCHRONIZING..." : "FETCH REFRESH LIVE REGISTRY"}
            </button>
          </div>

          {/* FETCHING LOADER OR CARDS LIST */}
          {isFetchingTemplates ? (
            <div className="p-8 border border-cyan-500/30 rounded-2xl bg-purple-950/5 min-h-[300px] flex flex-col items-center justify-center text-center font-mono relative overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-[1.5px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent animate-pulse" />
              <Loader2 className="w-8 h-8 text-cyan-400 animate-spin mb-4" />
              <span className="text-white text-xs font-black uppercase tracking-[0.2em] animate-pulse mb-2">INTEGRATING REGISTRY SECURE ASSETS ENGINES</span>
              
              <div className="w-full max-w-lg bg-black/80 border border-slate-850 rounded-lg p-3 text-left text-[8px] uppercase text-cyan-400 space-y-1 my-3 max-h-40 overflow-y-auto">
                {templateFetchLogs.map((log, idx) => (
                  <div key={idx} className="flex gap-2">
                    <span className="text-slate-500">[{idx+1}]</span>
                    <span className="font-extrabold">{log}</span>
                  </div>
                ))}
              </div>
              <span className="text-slate-400 text-[8px]">CLOUDSYNC ACTIVE DIRECT SYSTEM HANDSHAKE (SC887095)</span>
            </div>
          ) : (
            <div>
              {/* FILTER OVERVIEW */}
              <div className="flex items-center justify-between font-mono text-[8.5px] text-slate-400 uppercase mb-4 border-b border-slate-900 pb-2">
                <span>Displaying templates from registry (live cache)</span>
                <span>Active count: {
                  automationTemplates.filter(tmp => {
                    const matchesSearch = tmp.name.toLowerCase().includes(templateSearchQuery.toLowerCase()) || 
                                          tmp.recommendedUsage.toLowerCase().includes(templateSearchQuery.toLowerCase());
                    const matchesRatio = templateFilterRatio === "ALL" || tmp.outputAspectRatio === templateFilterRatio;
                    return matchesSearch && matchesRatio;
                  }).length
                } of {automationTemplates.length}</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {automationTemplates.filter(tmp => {
                  const matchesSearch = tmp.name.toLowerCase().includes(templateSearchQuery.toLowerCase()) || 
                                        tmp.recommendedUsage.toLowerCase().includes(templateSearchQuery.toLowerCase());
                  const matchesRatio = templateFilterRatio === "ALL" || tmp.outputAspectRatio === templateFilterRatio;
                  return matchesSearch && matchesRatio;
                }).map(tmp => (
                  <div 
                    key={tmp.id} 
                    className="bg-[#020306]/85 border border-slate-850 rounded-xl p-4.5 hover:border-amber-500/45 hover:shadow-[0_0_15px_rgba(245,158,11,0.08)] transition-all flex flex-col justify-between group relative"
                  >
                    <div>
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-white font-sans font-bold text-xs uppercase group-hover:text-amber-300 transition-colors">{tmp.name}</span>
                        <span className="bg-amber-500/10 border border-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded font-mono text-[7px] uppercase tracking-wider font-extrabold">{tmp.outputAspectRatio}</span>
                      </div>
                      
                      <div className="space-y-1.5 font-mono text-[8px] text-slate-400 uppercase">
                        <div><strong className="text-slate-350">OVERLAYS:</strong> {tmp.textOverlays}</div>
                        <div><strong className="text-slate-350">MOTION STYLE:</strong> {tmp.motionStyle}</div>
                        <div><strong className="text-slate-350">CTA OVERLAY:</strong> {tmp.ctaStyle}</div>
                        <div><strong className="text-slate-350">AUDIO Track:</strong> {tmp.voiceoverLength}</div>
                        <div><strong className="text-slate-350">SCENES:</strong> {tmp.sceneCount} clips</div>
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-slate-900/60 flex flex-col gap-2">
                      <div className="flex items-center justify-between text-[7px] font-mono text-slate-500 uppercase">
                        <span>USAGE:</span>
                        <span className="text-slate-400 font-bold">{tmp.recommendedUsage}</span>
                      </div>

                      <div className="grid grid-cols-2 gap-1.5 mt-1">
                        <button
                          onClick={() => {
                            setFormVideoType(tmp.name.replace(" Template", ""));
                            setFormAspectRatio(tmp.outputAspectRatio);
                            setFormDuration(tmp.sceneDuration);
                            setFormCta(tmp.ctaStyle.includes("No primary") ? "None" : "Book Consultation");
                            setFormScenePrompt(tmp.motionStyle + " + " + tmp.textOverlays);
                            setFormVideoTitle(`${brandAssets.company} ${tmp.name.replace(" Template", "")}`);
                            
                            setAuditLogs([
                              { id: "log-tmp-" + Date.now(), time: new Date().toTimeString().split(' ')[0], user: "Director Admin", action: `Loaded template variables for: "${tmp.name}"`, status: "SUCCESS" },
                              ...auditLogs
                            ]);
                            setDuplicateWarning(`SUCCESS: Preset initialized completely. Navigate to '2. Video Generator' to compile.`);
                          }}
                          className="bg-amber-500 hover:bg-amber-400 text-black px-2 py-1.5 text-[8px] font-mono tracking-wider font-bold rounded uppercase cursor-pointer transition-colors text-center"
                        >
                          APPLY PRESET
                        </button>

                        <button
                          onClick={() => {
                            // Instant layout generation
                            const newVidId = "auto-vid-" + Date.now();
                            const newVid = {
                              id: newVidId,
                              title: `${brandAssets.company || "NexoraX"} ${tmp.name.replace(" Template", "")} #${Math.floor(100 + Math.random() * 900)}`,
                              type: tmp.name.replace(" Template", ""),
                              duration: tmp.sceneDuration,
                              aspectRatio: tmp.outputAspectRatio,
                              status: "Draft",
                              category: tmp.outputAspectRatio === "16:9" ? "vid-pack-3-default" : "auto-vid-1",
                              script: {
                                hook: `Unlocking high-conversion asset templates on NexoraX!`,
                                problem: `Manual editing waste overhead leaks margin.`,
                                solution: `Direct cloud template-based compiler output.`,
                                benefit: `Scale video catalogs 10x faster.`,
                                cta: brandAssets.phone || "07359062464"
                              },
                              voiceoverScript: `SMEs attention: Initiate instant generation pipelines with ${tmp.name}. Safe SEO index guaranteed. Call corporate today.`,
                              subtitleText: `0.8s system speed. Built-in SEO shield. Start compiling instantly.`,
                              thumbnailPrompt: `${tmp.thumbnailStyle || "Bento grid stylized icon spotlight"} crafted for enterprise branding`,
                              createdAt: new Date().toTimeString().split(' ')[0]
                            };
                            setVideosList([newVid, ...videosList]);
                            setAuditLogs([
                              { id: "log-gen-inst-" + Date.now(), time: new Date().toTimeString().split(' ')[0], user: "Director Admin", action: `Instantly drafted video card utilizing template: "${tmp.name}"`, status: "SUCCESS" },
                              ...auditLogs
                            ]);
                            setDuplicateWarning(`SUCCESS: Instantly drafted '${newVid.title}' in the queue. Press '4. Video Queue' to manage.`);
                          }}
                          className="bg-purple-900 hover:bg-purple-850 text-purple-200 border border-purple-500/40 px-2 py-1.5 text-[8px] font-mono tracking-normal font-bold rounded uppercase cursor-pointer transition-colors text-center"
                        >
                          DRAFT INSTANT
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 6. GUIDES & VIDEOS SYNC PANEL */}
      {activeSection === 'GUIDES_SYNC' && (
        <div className="bg-black/40 border border-slate-805/85 rounded-2xl p-6 backdrop-blur-sm">
          <div className="border-b border-slate-800 pb-4 mb-6">
            <h3 className="text-white text-md font-sans font-extrabold uppercase tracking-widest flex items-center gap-2">
              <Link className="w-5 h-5 text-blue-400" /> 6. GUIDE VIDEOS & SYNC CO-DIRECTOR
            </h3>
            <p className="text-slate-400 text-xs mt-1">
              Associate synthesized promotional guide videos directly to Workspace interactive tutorials, updating client portals seamlessly.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { id: "welcome-to-nexorax", name: "Welcome to NexoraX Orientation", desc: "Introductory guide video displaying communicating endpoints.", defaultAssigned: "vid-pack-1-default" },
              { id: "nexus-search-guide", name: "Nexus Search guide", desc: "Sitemap index queries guide.", defaultAssigned: "vid-pack-2-default" },
              { id: "nexus-hosting-guide", name: "Nexus Hosting Console Walkthrough", desc: "Step-by-step guide video configuration of dedicated bare metal servers.", defaultAssigned: "vid-pack-3-default" }
            ].map(gd => {
              const assignedVid = videosList.find(v => v.guideId === gd.id) || videosList[0];
              return (
                <div key={gd.id} className="p-4 bg-[#020306] rounded-xl border border-slate-850 flex flex-col justify-between">
                  <div>
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-white font-sans font-bold text-xs">{gd.name}</span>
                      <span className="bg-blue-500/10 border border-blue-500/30 text-blue-400 px-1.5 py-0.5 rounded font-mono text-[7px] uppercase font-black">{gd.id}</span>
                    </div>
                    <p className="font-mono text-[8px] text-slate-500 leading-relaxed uppercase mb-4">{gd.desc}</p>
                    
                    <div className="bg-black/40 p-2.5 rounded border border-slate-900 flex flex-col space-y-1">
                      <span className="font-mono text-[7.5px] text-slate-400 uppercase">BOUND VIDEO ATTACHMENT:</span>
                      <span className="text-xs font-semibold text-white truncate">{assignedVid ? assignedVid.title : "None Bound"}</span>
                      {assignedVid && (
                        <div className="flex justify-between items-center text-[7.5px] font-mono text-slate-500 mt-1 uppercase">
                          <span>FORMAT: {assignedVid.aspectRatio}</span>
                          <span className="text-emerald-400 font-extrabold">{assignedVid.status}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-900 flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setDuplicateWarning(`SYNCED: Refreshed mapping for Guide: ${gd.id}`);
                        setAuditLogs([
                          { id: "log-g-" + Date.now(), time: new Date().toTimeString().split(' ')[0], user: "Director Sync", action: `Forced data rebinding for guide ID: ${gd.id}`, status: "SUCCESS" },
                          ...auditLogs
                        ]);
                      }}
                      className="border border-slate-800 hover:border-slate-700 font-mono text-[8px] px-2.5 py-1.5 rounded uppercase text-slate-400 font-bold"
                    >
                      RE-MAP
                    </button>
                    <button
                      onClick={() => {
                        setActiveVideoObject(assignedVid);
                        setDuplicateWarning(`PLAYING: Loading screen orientation guide video to monitor.`);
                      }}
                      className="bg-blue-600 hover:bg-blue-500 text-white font-mono text-[8px] px-2.5 py-1.5 rounded uppercase font-extrabold"
                    >
                      PREVIEW LOOP
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 7. HOMEPAGE HERO MEDIA MANAGER */}
      {activeSection === 'HOMEPAGE_MEDIA' && (
        <div className="bg-black/40 border border-slate-800 rounded-2xl p-6 backdrop-blur-sm">
          <div className="border-b border-slate-800 pb-4 mb-6">
            <h3 className="text-white text-md font-sans font-extrabold uppercase tracking-widest flex items-center gap-2">
              <Home className="w-5 h-5 text-teal-400" /> 7. HOMEPAGE HERO MEDIA MANAGER
            </h3>
            <p className="text-slate-400 text-xs mt-1">
              Select and assign the background looping cinematic video rendered dynamically for the landing homepage-hero.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Loop Select list */}
            <div className="space-y-3 bg-[#04060d]/70 p-4 rounded-xl border border-slate-850">
              <span className="font-mono text-[9px] text-teal-400 uppercase tracking-widest font-black block mb-2">// SELECTED HERO LOOPS</span>
              
              {videosList.filter(v => v.aspectRatio === "16:9").map(vl => (
                <div 
                  key={vl.id}
                  onClick={() => {
                    setActiveHomepageHeroId(vl.id);
                    setDuplicateWarning(`HOMEPAGE SYNC: Associated Loop Id ${vl.id} as current homepage background.`);
                  }}
                  className={`p-2.5 rounded border cursor-pointer hover:border-teal-500/40 transition-colors flex flex-col space-y-1 bg-black/60 ${activeHomepageHeroId === vl.id ? 'border-teal-400 ring-1 ring-teal-400/20' : 'border-slate-850'}`}
                >
                  <span className="text-white text-xs font-bold font-sans">{vl.title}</span>
                  <span className="font-mono text-[7px] text-slate-500 uppercase">SUGGESTED FILENAME: {vl.suggestedFilename || `${vl.id}.mp4`}</span>
                </div>
              ))}
            </div>

            {/* Animation customizer */}
            <div className="bg-slate-950/40 border border-slate-850 p-4.5 rounded-xl space-y-4">
              <span className="font-mono text-[9px] text-teal-400 uppercase tracking-widest font-black block">// OVERLAY GLOW STYLE</span>
              
              <div className="flex flex-col space-y-2">
                <label className="text-[8px] font-mono uppercase text-slate-400">Selected Interactive Template</label>
                <select
                  value={heroOverlayTemplate}
                  onChange={(e) => setHeroOverlayTemplate(e.target.value)}
                  className="bg-black border border-slate-850 text-xs py-2 px-2.5 text-white focus:outline-none focus:border-teal-400 rounded-lg font-mono"
                >
                  <option value="Cosmic Grid Matrix">Cosmic Grid Matrix (Cyan Dots)</option>
                  <option value="Aura Cyberpunk Gradient">Aura Cyberpunk Gradient (Purple Gloss)</option>
                  <option value="Minimal Corporate Glassmorphism">Minimal Corporate Glassmorphism</option>
                </select>
              </div>

              <div className="p-3 bg-[#020306] rounded border border-slate-900 font-mono text-[8px] uppercase space-y-1.5 text-slate-400">
                <span className="font-bold text-teal-400 block mb-1">PROPOSED SITEMAP INDEX:</span>
                <div>HOMEPAGE TARGET: index.html</div>
                <div>BOUND OVERLAY TYPE: {heroOverlayTemplate.toUpperCase()}</div>
                <div>SECURE DIRECT BILLING: NH448796B</div>
              </div>
            </div>

            {/* Preview visualizer */}
            <div className="bg-zinc-950 border border-[#1e293b] p-4 rounded-xl flex flex-col justify-between">
              <div>
                <span className="font-mono text-[9px] text-teal-400 uppercase tracking-widest block mb-2 font-black">// LOOP PREVIEW HUD</span>
                <div className="bg-black rounded-lg aspect-video border border-slate-850 flex flex-col items-center justify-center p-4 relative overflow-hidden">
                  <div className="absolute inset-0 bg-[linear-gradient(to_right,#083344_1px,transparent_1px)] bg-[size:12px_12px] opacity-15" />
                  <span className="text-[7.5px] font-mono text-cyan-400 uppercase font-black tracking-widest z-10">[ ACTIVE HERO LOOP ]</span>
                  <p className="text-white text-xs font-sans font-black text-center z-10 mt-1 uppercase">
                    {videosList.find(v => v.id === activeHomepageHeroId)?.title || "Suite Promo loop"}
                  </p>
                  <span className="text-[6.5px] font-mono text-slate-500 z-10 block mt-2">BOUND OVERLAY: {heroOverlayTemplate}</span>
                </div>
              </div>

              <button
                onClick={() => {
                  setDuplicateWarning("SUCCESS: Homepage Hero Background changed and compiled on primary public sitemap completely.");
                  setAuditLogs([
                    { id: "log-h-" + Date.now(), time: new Date().toTimeString().split(' ')[0], user: "Director Home", action: "Outbound Hero loop changed to " + activeHomepageHeroId, status: "SUCCESS" },
                    ...auditLogs
                  ]);
                }}
                className="mt-4 bg-teal-500 hover:bg-teal-400 text-black py-2 rounded-lg font-mono text-xs font-bold uppercase transition-all"
              >
                APPLY HOMEPAGE DIRECTIVE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 8. SOCIAL DISTRIBUTION QUEUE */}
      {activeSection === 'SOCIAL_QUEUE' && (
        <div className="bg-black/40 border border-slate-800 rounded-2xl p-6 backdrop-blur-sm">
          <div className="border-b border-slate-800 pb-4 mb-6">
            <h3 className="text-white text-md font-sans font-extrabold uppercase tracking-widest flex items-center gap-2">
              <Share2 className="w-5 h-5 text-pink-400" /> 8. SOCIAL MEDIA CHANNELS QUEUE
            </h3>
            <p className="text-slate-400 text-xs mt-1">
              Configure scheduled short vertical videos, write viral caption overlays, and monitor cross-channel distribution pipelines.
            </p>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {socialQueue.map((item, idx) => (
                <div key={item.id} className="bg-[#020306] border border-slate-850 p-4.5 rounded-xl flex flex-col justify-between hover:border-pink-500/20 transition-all group">
                  <div>
                    <div className="flex justify-between items-center mb-2.5">
                      <span className="bg-pink-500/10 border border-pink-500/20 text-pink-400 px-2 py-0.5 rounded font-mono text-[7px] uppercase font-black">{item.platform}</span>
                      <span className="text-[7.5px] font-mono text-slate-500">{item.scheduledTime}</span>
                    </div>
                    
                    <span className="text-slate-100 font-sans font-bold text-xs block mb-1 group-hover:text-pink-300 transition-colors">{item.title}</span>
                    <p className="font-mono text-[8px] text-slate-400 leading-relaxed uppercase bg-black/50 p-2 border border-slate-900 rounded select-all mb-4">
                      {item.caption}
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-t border-slate-900 pt-3 mt-1.5 font-mono text-[7.5px]">
                    <div className="flex items-center gap-1.5">
                      {item.status !== "Posted" ? (
                        <span className="text-[7.5px] font-mono text-purple-400 bg-purple-950/40 border border-purple-500/20 px-2 py-1 rounded inline-flex items-center gap-1">
                          🔒 REQUIRES SUPERVISOR APPROVAL
                        </span>
                      ) : (
                        <span className="text-[7.5px] font-mono text-emerald-400 bg-emerald-950/40 border border-emerald-500/20 px-2 py-1 rounded inline-flex items-center gap-1">
                          ✓ POSTED SUCCESSFULLY
                        </span>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setDuplicateWarning(`PUBLISHED SOCIAL: Pushed vertical clip immediately to ${item.platform} API.`);
                          setSocialQueue(socialQueue.map(s => s.id === item.id ? { ...s, status: "Posted", isPosted: true } : s));
                          setAuditLogs([
                            { id: "log-s-" + Date.now(), time: new Date().toTimeString().split(' ')[0], user: "Director Social", action: `Direct Pushed vertical clip to ${item.platform}`, status: "SUCCESS" },
                            ...auditLogs
                          ]);
                        }}
                        disabled={item.status === "Posted"}
                        className={`px-3 py-1.5 rounded uppercase font-bold tracking-wider ${item.status === "Posted" ? 'bg-slate-900 text-slate-600 border border-slate-800' : 'bg-pink-500 hover:bg-pink-400 text-black font-black'}`}
                      >
                        {item.status === "Posted" ? "POSTED" : "MANUAL POSTING HELPER"}
                      </button>
                      <button
                        onClick={() => {
                          setDuplicateWarning(`REMOVED SLOT: Discarded social item ${item.id}`);
                          setSocialQueue(socialQueue.filter(s => s.id !== item.id));
                        }}
                        className="border border-[#1e293b] hover:border-rose-500/30 text-slate-500 hover:text-rose-400 px-2 py-1.5 rounded uppercase font-bold"
                      >
                        DISCARD
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-black/50 border border-slate-900 rounded-xl p-4 flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-pink-500/10 rounded border border-pink-500/20 text-pink-400 shrink-0">
                  <Share2 className="w-5 h-5" />
                </div>
                <div className="font-mono text-[9px] uppercase text-slate-400 space-y-0.5 leading-relaxed">
                  <span className="font-black text-white">BULK OUTBOUND DISTRIBUTION CONFLICT WARNING:</span>
                  <div>Ensure all hashtags split conform to the direct billing records scheme placement fees absorbed under SC887095 protocols.</div>
                </div>
              </div>
              <button
                onClick={() => {
                  setDuplicateWarning("SUCCESS: Social Queue cache refreshed completely.");
                }}
                className="bg-slate-800 hover:bg-slate-700 text-slate-200 px-4 py-2 rounded-lg font-mono text-xs font-bold uppercase transition-all tracking-widest"
              >
                REFRESH SOCIAL API FEED
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 9. PUBLISH SCHEDULER */}
      {activeSection === 'PUBLISH_SCHEDULER' && (
        <div className="bg-black/40 border border-slate-800 rounded-2xl p-6 backdrop-blur-sm">
          <div className="border-b border-slate-800 pb-4 mb-6">
            <h3 className="text-white text-md font-sans font-extrabold uppercase tracking-widest flex items-center gap-2">
              <Clock className="w-5 h-5 text-rose-450" /> 9. SCHEDULED GENERATION CHECKS
            </h3>
            <p className="text-slate-400 text-xs mt-1">
              Scheduled generation checks managing automatic media rendering pipelines.
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-3">
              {schedulers.map(sch => (
                <div key={sch.id} className="p-4 bg-zinc-950 border border-slate-850 hover:border-rose-500/20 rounded-xl transition-all flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-3.5">
                    <div className="p-2 h-9 w-9 rounded-lg bg-rose-500/5 border border-rose-500/20 flex items-center justify-center text-rose-400">
                      <Clock className="w-4 h-4" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-white font-sans font-bold text-xs">{sch.name}</span>
                      <span className="font-mono text-[8px] text-slate-500 mt-0.5 uppercase tracking-wide">INTERVAL: {sch.frequency}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center font-mono text-[8.5px] uppercase text-slate-400">
                    <div><span>LAST RUN:</span> <strong className="text-slate-250 block mt-0.5">{sch.lastRun}</strong></div>
                    <div><span>NEXT RUN:</span> <strong className="text-slate-250 block mt-0.5">{sch.nextRun}</strong></div>
                    <div><span>TYPE:</span> <strong className="text-slate-250 block mt-0.5">{sch.outputType}</strong></div>
                    <div><span>MODE:</span> <strong className="text-slate-250 block mt-0.5">{sch.mode}</strong></div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setSchedulers(schedulers.map(s => s.id === sch.id ? { ...s, active: !s.active } : s));
                        setDuplicateWarning(`SCHEDULER TOGGLED: Changed status for ${sch.name}`);
                      }}
                      className={`px-3 py-1.5 rounded font-mono text-[8px] font-black uppercase border ${sch.active ? 'bg-rose-500/10 border-rose-500/30 text-rose-400' : 'bg-[#020306] border-slate-800 text-slate-600'}`}
                    >
                      {sch.active ? "ENABLED" : "MUTED"}
                    </button>
                    <button
                      onClick={() => {
                        runConsoleSim(`SIMULATING TRIGGER NOW FOR: ${sch.name.toUpperCase()}`, [
                          `[TRIGGER] Triggering immediate compilation for: ${sch.name}`,
                          `[PARSING] Resolving Company parameter: ${brandAssets.company}`,
                          `[FINISHING] Finished task. Writing index log to Google Workspace.`
                        ]);
                        setDuplicateWarning(`TRIGGERED: Dispatched compile task: ${sch.name}`);
                      }}
                      className="bg-purple-600 hover:bg-purple-500 text-white font-mono text-[8px] px-3 py-1.5 rounded uppercase font-black"
                    >
                      RUN NOW
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {consoleIsRunning && (
              <div className="bg-black border border-cyan-500/20 rounded-xl p-4 font-mono text-[9px] text-cyan-400 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-extrabold uppercase animate-pulse">{consoleHeader}</span>
                  <span>{consoleProgress}%</span>
                </div>
                <div className="w-full bg-slate-900 h-1 rounded-full overflow-hidden">
                  <div className="bg-cyan-400 h-full transition-all duration-300" style={{ width: `${consoleProgress}%` }} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 10. VIDEO OVERVIEW DASHBOARD */}
      {activeSection === 'OVERVIEW_DASHBOARD' && (
        <div className="space-y-6">
          
          {/* ANALYTICS KPI OVERVIEW */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: "Videos Generated", value: videosList.length, label: "All Active Content Assets", color: "text-purple-400" },
              { title: "Videos Published", value: videosList.filter(v => v.status === "Published").length, label: "Live Outbound Links", color: "text-emerald-400" },
              { title: "Needs Review", value: videosList.filter(v => v.status === "Needs Review").length, label: "Awaiting Admin Action", color: "text-amber-400 animate-pulse" },
              { title: "Failed Renders", value: videosList.filter(v => v.status === "Failed" || v.status === "Failed Halt" || v.status === "Failed").length, label: "Blocked Lifecycle Pipelines", color: "text-rose-450" }
            ].map((kpi, idx) => (
              <div key={idx} className="p-5 rounded-2xl bg-purple-950/20 border border-cyan-500/30 backdrop-blur-md shadow-[0_0_15px_rgba(6,182,212,0.15)] flex flex-col justify-between transition-all hover:scale-[1.02] hover:border-cyan-400">
                <span className="font-mono text-[9px] uppercase tracking-wider text-slate-400 font-bold">{kpi.title}</span>
                <span className={`text-3xl font-black mt-2 font-mono tracking-tight ${kpi.color}`}>{kpi.value}</span>
                <span className="font-mono text-[8px] uppercase tracking-widest text-slate-500 mt-2">{kpi.label}</span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Visual monitor display preview */}
            <div className="bg-black/60 border border-slate-800 rounded-2xl p-5 relative overflow-hidden backdrop-blur-md flex flex-col">
              <div className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-0.5 rounded bg-cyan-950/40 border border-cyan-500/20 text-[9px] font-mono text-cyan-400 uppercase tracking-widest">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" /> LIVE TRACK MONITOR
              </div>
              
              <h4 className="text-white text-xs font-mono uppercase tracking-widest font-bold mb-4 flex items-center gap-2">
                <Video className="w-4 h-4 text-cyan-400" /> ACTIVE PREVIEW CONSOLE
              </h4>

              <div className="bg-[#020306] rounded-xl border border-slate-800/80 aspect-video w-full relative overflow-hidden flex flex-col items-center justify-center p-4">
                <div className="absolute inset-0 bg-[radial-gradient(#080e1a_1px,transparent_1px)] [background-size:16px_16px] opacity-40" />
                
                {activeVideoObject ? (
                  <div className="w-full h-full relative flex flex-col items-center justify-center">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-48 h-48 rounded-full border border-purple-500/20 animate-[spin_10s_linear_infinite] flex items-center justify-center p-2">
                        <div className="w-full h-full rounded-full border border-dashed border-cyan-500/30 animate-[spin_4s_linear_infinite_reverse] flex items-center justify-center p-4">
                          <div className="w-full h-full rounded-full border border-dotted border-amber-500/20" />
                        </div>
                      </div>
                    </div>

                    <div className="absolute top-2 left-2 z-10 font-mono text-[9px] text-slate-500 flex flex-col">
                      <span>PROJECT ID: {activeVideoObject.id}</span>
                      <span>FORMAT: {activeVideoObject.aspectRatio}</span>
                      <span>PRIORITY: {activeVideoObject.priority}</span>
                    </div>

                    <div className="relative z-10 text-center space-y-2 max-w-md p-4 bg-black/50 border border-slate-800/50 rounded-xl backdrop-blur-sm">
                      <p className="text-cyan-400 font-mono text-[9px] uppercase tracking-widest font-bold">[[ {activeVideoObject.type} ]]</p>
                      <h5 className="text-white text-md font-sans font-extrabold tracking-tight">{activeVideoObject.title}</h5>
                      <p className="text-slate-400 text-[10px] italic">"{activeVideoObject.scenePrompt || 'Branded Motion scene'}"</p>
                    </div>

                    <div className="absolute bottom-6 left-4 right-4 z-10 bg-black/80 border border-cyan-500/30 rounded-lg p-2 flex flex-col items-center justify-center backdrop-blur-sm">
                      <span className="text-[7.5px] font-mono tracking-widest text-cyan-400 font-black mb-1">CAPTION OVERLAYS TIMELINES:</span>
                      <span className="text-white font-mono text-[10px] text-center uppercase tracking-wider font-extrabold px-4 text-glow-cyan">
                        {activeVideoObject.subtitleText || "[BACKGROUND LOOP COMPONENT - NO CAPTIONS VALUE]"}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center space-y-3 z-10 text-center px-4">
                    <Loader2 className="w-6 h-6 text-slate-600 animate-spin" />
                    <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400">AWAITING CORRESPONDENCE SELECT</p>
                    <p className="font-mono text-[8px] uppercase text-slate-500 max-w-xs">Select any video item from the '4. Video Queue' or '7. Homepage Media' to inspect assets here.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Simulated Alerts & budget sync state */}
            <div className="bg-black/60 border border-slate-850 p-5 rounded-2xl flex flex-col justify-between">
              <div>
                <h4 className="text-white text-xs font-mono uppercase tracking-widest font-bold mb-4 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-purple-400" /> DIAL COMPLIANCE ALERTS
                </h4>
                
                <div className="space-y-3">
                  {simulatedAlerts.map(alert => (
                    <div key={alert.id} className="p-3 bg-slate-900/30 border border-slate-850 rounded-xl flex items-start gap-2 text-[9px] font-mono uppercase">
                      <AlertCircle className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${alert.type === 'CRITICAL' ? 'text-rose-400' : alert.type === 'WARNING' ? 'text-amber-400' : 'text-emerald-400'}`} />
                      <div className="flex-1 space-y-0.5">
                        <div className="flex justify-between font-black">
                          <span className={alert.type === 'CRITICAL' ? 'text-rose-450' : alert.type === 'WARNING' ? 'text-amber-450' : 'text-emerald-450'}>{alert.type} STATUS</span>
                          <span className="text-slate-500">{alert.time}</span>
                        </div>
                        <p className="text-slate-400 leading-relaxed">{alert.msg}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#020306] border border-slate-900 p-3 rounded-lg text-slate-500 font-mono text-[7.5px] uppercase mt-4">
                <strong>FINANCIAL DIRECTIVE:</strong> Your placement fees absorbed under SC887095 Zero-Budget Campaign Video Pack require an active 10% maintenance allocation track.
              </div>
            </div>

            {/* Admin Activity Logs */}
            <div className="bg-black/80 border border-slate-800 rounded-2xl p-5 relative font-mono text-[10.5px]">
              <div className="flex items-center justify-between border-b border-slate-850 pb-3 mb-4">
                <span className="font-bold text-white uppercase tracking-widest text-[9px] flex items-center gap-2">
                  <Cpu className="w-4 h-4 text-purple-400 animate-pulse" /> ADMIN ACTIVITY LOG
                </span>
                <span className="text-[7.5px] text-emerald-400 flex items-center gap-1 font-black">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> CLOUDSYNC ACTIVE
                </span>
              </div>

              <div className="bg-black/95 rounded-xl p-3 border border-slate-850 h-52 overflow-y-auto space-y-1 text-slate-400">
                <div>[SYSTEM INFO 11:00] Running Communications Node OS (SC887095)</div>
                <div>[SYSTEM INFO 11:02] Compliance frequency 08000 622827 active</div>
                <div>[SYSTEM INFO 11:05] Standard direct-billing NI NH448796B initialized</div>
                {auditLogs.map((log, idx) => (
                  <div key={idx} className="flex gap-2">
                    <span className="text-slate-600">[{log.time}]</span>
                    <span className="text-purple-400">({log.user})</span>
                    <span className="text-slate-350 line-clamp-1">{log.action}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
